import { DonationType } from '@nkr/enums/donation-type';
import { IMicrositeRecipientForList } from '@nkr/models/microsites/microsite-recipient-for-list';
import { useEffect, useState } from 'react';
import { Modal, Button, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';

interface IMicrositePatientOption extends IMicrositeRecipientForList {
    label: string;
}

type TVoucherType = '' | DonationType;

interface IProps {
    show: boolean;
    donorName: string;
    patientList: IMicrositeRecipientForList[];
    onHide: () => void;
    onConfirm: (micrositeRecipientId: number, voucherType: DonationType | '') => void;
}

type TStep = 'select' | 'confirm';

interface IState {
    step: TStep;
    selected: IMicrositePatientOption[];
    voucherType: DonationType | '';
}

const initialState: IState = {
    step: 'select',
    selected: [],
    voucherType: ''
};

const VoucherHolderMicrositeRecipientModal = (props: IProps) => {
    const [state, setState] = useState<IState>(initialState);

    useEffect(() => {
        if (props.show) {
            setState(initialState);
        }
    }, [props.show]);

    const patientOptions: IMicrositePatientOption[] = Array.isArray(props.patientList)
        ? props.patientList.map(p => ({
            ...p,
            label: [p.firstName, p.lastName].filter(Boolean).join(' ') || p.shortCode || ''
        }))
        : [];

    const handleSelectionChange = (selected: IMicrositePatientOption[]) => {
        setState(prev => ({ ...prev, selected }));
    };

    const handleVoucherTypeChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        const v = e.currentTarget.value;
        setState(prev => ({ ...prev, voucherType: v == '' ? '' : Number(v) as DonationType }));
    };

    const handleNext = () => {
        if (state.selected.length > 0 && state.voucherType != '') {
            setState(prev => ({ ...prev, step: 'confirm' }));
        }
    };

    const handleBack = () => {
        setState(prev => ({ ...prev, step: 'select' }));
    };

    const handleConfirm = () => {
        if (state.selected.length > 0 && state.voucherType != '') {
            props.onConfirm(state.selected[0].id, state.voucherType);
        }
    };

    const selectedPatient = state.selected.length > 0 ? state.selected[0] : null;
    const voucherTypeLabel = state.voucherType == DonationType.StandardVoucher ? 'Standard Voucher Holder' : state.voucherType == DonationType.FamilyVoucher ? 'Family Voucher Holder' : '';
    const isNextDisabled = state.selected.length == 0 || state.voucherType == '';

    return (
        <Modal show={props.show} onHide={props.onHide} backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {state.step == 'select' ? 'Add Microsite Voucher Holder' : 'Confirm New Voucher Holder'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{ position: 'relative', minHeight: 220 }}>
                    {/* Step 1 */}
                    <div style={{
                        transition: 'opacity 0.25s ease, transform 0.25s ease',
                        opacity: state.step == 'select' ? 1 : 0,
                        transform: state.step == 'select' ? 'translateX(0)' : 'translateX(-30px)',
                        pointerEvents: state.step == 'select' ? 'auto' : 'none',
                        position: state.step == 'select' ? 'relative' : 'absolute',
                        top: 0, left: 0, right: 0
                    }}>
                        <p>
                            Please enter the name of the Microsite patient you would like to add as a voucher holder for: <strong>{props.donorName}</strong>
                        </p>

                        <FormGroup>
                            <Typeahead
                                id='microsite-patient-typeahead'
                                clearButton
                                labelKey='label'
                                options={patientOptions}
                                selected={state.selected}
                                filterBy={['firstName', 'lastName', 'shortCode']}
                                placeholder='Type Microsite Patient Name'
                                renderInput={(inputProps) => (
                                    <FormControl {...inputProps} />
                                )}
                                renderMenuItemChildren={(option: IMicrositePatientOption) => (
                                    <div style={{ borderBottom: '1px solid #ddd', paddingBottom: 5 }}>
                                        <div>Microsite (Short Code) ID: {option.shortCode}</div>
                                        {option.firstName && option.lastName && (
                                            <div><small>Name: {option.firstName} {option.lastName}</small></div>
                                        )}
                                        <div><small>DOB: {option.birthdate}</small></div>
                                    </div>
                                )}
                                onChange={handleSelectionChange}
                            />
                        </FormGroup>

                        <FormGroup>
                            <FormControl
                                componentClass='select'
                                value={state.voucherType}
                                onChange={handleVoucherTypeChange}
                            >
                                <option value=''>What type of Voucher?</option>
                                <option value={2}>Standard Voucher Holder</option>
                                <option value={4}>Family Voucher Holder</option>
                            </FormControl>
                        </FormGroup>

                        <div style={{ marginTop: 20, textAlign: 'right' }}>
                            <Button
                                bsStyle="primary"
                                disabled={isNextDisabled}
                                onClick={handleNext}
                                style={{ marginRight: 10 }}
                            >
                                Continue
                            </Button>
                            <Button onClick={props.onHide}>Cancel</Button>
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div style={{
                        transition: 'opacity 0.25s ease, transform 0.25s ease',
                        opacity: state.step == 'confirm' ? 1 : 0,
                        transform: state.step == 'confirm' ? 'translateX(0)' : 'translateX(30px)',
                        pointerEvents: state.step == 'confirm' ? 'auto' : 'none',
                        position: state.step == 'confirm' ? 'relative' : 'absolute',
                        top: 0, left: 0, right: 0
                    }}>
                        <p>
                            <strong>({selectedPatient?.firstName} {selectedPatient?.lastName})</strong> will be added as a <strong>({voucherTypeLabel})</strong> for <strong>({props.donorName})</strong>.
                        </p>
                        <p>If this is correct, please click confirm below. If incorrect, select cancel.</p>

                        <div style={{ marginTop: 20, textAlign: 'right' }}>
                            <Button
                                bsStyle="success"
                                onClick={handleConfirm}
                                style={{ marginRight: 10 }}>
                                Confirm
                            </Button>
                            <Button bsStyle="primary"
                                onClick={handleBack}
                                style={{ marginRight: 10 }}>
                                Back
                            </Button>
                            <Button bsStyle="primary" onClick={props.onHide}>Cancel</Button>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default VoucherHolderMicrositeRecipientModal;